import React, { useCallback, useMemo, useEffect, useRef } from "react";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Background,
  MiniMap,
  NodeTypes,
  Handle,
  Position,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Box,
  Typography,
  Icon,
  alpha,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { IChartOfAccount } from "../../../accounting.interface";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

interface AccountFlowViewProps {
  accounts: IChartOfAccount[];
  selectedAccount?: IChartOfAccount;
  onNodeClick: (account: IChartOfAccount) => void;
  onAddSubAccount: (parentAccount: IChartOfAccount) => void;
  onAddNewAtLevel: (level: number, parentAccountId?: string) => void;
}

const getAccountTypeColor = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "#10B981";
    case "Liability":
      return "#EF4444";
    case "Equity":
      return "#3B82F6";
    case "Revenue":
      return "#8B5CF6";
    case "Expense":
      return "#F59E0B";
    default:
      return "#6B7280";
  }
};

const getAccountTypeIcon = (accountType: string) => {
  switch (accountType) {
    case "Asset":
      return "account_balance_wallet";
    case "Liability":
      return "credit_card";
    case "Equity":
      return "trending_up";
    case "Revenue":
      return "monetization_on";
    case "Expense":
      return "money_off";
    default:
      return "account_balance";
  }
};

// Custom Node Component
const AccountNode: React.FC<{
  data: {
    account: IChartOfAccount;
    onNodeClick: (account: IChartOfAccount) => void;
    onAddSubAccount: (parentAccount: IChartOfAccount) => void;
  };
}> = ({ data }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { account, onNodeClick, onAddSubAccount } = data;
  const accountColor = getAccountTypeColor(account.accountType);
  const accountIcon = getAccountTypeIcon(account.accountType);
  const isInactive = account.isActive === false;

  const handleNodeClick = () => {
    if (!isInactive) {
      onNodeClick(account);
    }
  };

  const handleAddSubAccount = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isInactive) {
      onAddSubAccount(account);
    }
  };

  return (
    <Box
      onClick={handleNodeClick}
      sx={{
        minWidth: 200,
        maxWidth: 280,
        p: 2,
        bgcolor: isInactive
          ? alpha(theme.palette.grey[500], 0.1)
          : "background.paper",
        borderRadius: 2,
        border: `2px solid ${alpha(
          isInactive ? theme.palette.grey[500] : accountColor,
          0.3
        )}`,
        boxShadow: isInactive
          ? `0 2px 6px ${alpha(theme.palette.grey[500], 0.1)}`
          : `0 4px 12px ${alpha(accountColor, 0.15)}`,
        cursor: isInactive ? "not-allowed" : "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        opacity: isInactive ? 0.6 : 1,
        filter: isInactive ? "grayscale(70%)" : "none",
        "&:hover": isInactive
          ? {}
          : {
              borderColor: accountColor,
              boxShadow: `0 6px 20px ${alpha(accountColor, 0.25)}`,
              transform: "translateY(-2px)",
            },
      }}
    >
      {/* Inactive Badge */}
      {isInactive && (
        <Box
          sx={{
            position: "absolute",
            top: -8,
            left: 8,
            bgcolor: theme.palette.error.main,
            color: "white",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.625rem",
            fontWeight: 600,
            zIndex: 10,
          }}
        >
          INACTIVE
        </Box>
      )}

      {/* Add Sub-Account Button - Bottom Right Corner */}
      {!isInactive &&
      hasPermission(
        "accounting.chart_of_accounts.create",
        user?.newPermissions,
        user?.assignment_details?.id,
        user?.is_store_owner
      ) ? (
        <Tooltip title="Add Sub-Account" arrow placement="top">
          <span>
            <IconButton
              size="small"
              onClick={handleAddSubAccount}
              disabled={
                !hasPermission(
                  "accounting.chart_of_accounts.create",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                position: "absolute",
                bottom: -8,
                right: -8,
                width: 24,
                height: 24,
                bgcolor: theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                },
                zIndex: 10,
              }}
            >
              <Icon sx={{ fontSize: 14 }}>add</Icon>
            </IconButton>{" "}
          </span>
        </Tooltip>
      ) : null}

      {/* Node Content */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: alpha(
              isInactive ? theme.palette.grey[500] : accountColor,
              0.1
            ),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            sx={{
              fontSize: 18,
              color: isInactive ? theme.palette.grey[500] : accountColor,
            }}
          >
            {isInactive ? "block" : accountIcon}
          </Icon>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: isInactive ? "text.disabled" : "text.primary",
              lineHeight: 1.2,
              mb: 0.5,
              textDecoration: isInactive ? "line-through" : "none",
            }}
            noWrap
          >
            {account.accountName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isInactive ? "text.disabled" : "text.secondary",
              fontFamily: "monospace",
              fontSize: "0.75rem",
            }}
          >
            {account.accountCode}
          </Typography>
        </Box>
      </Box>

      {/* Account Type Badge */}
      <Chip
        label={account.accountType}
        size="small"
        sx={{
          height: 20,
          fontSize: "0.75rem",
          bgcolor: alpha(
            isInactive ? theme.palette.grey[500] : accountColor,
            0.1
          ),
          color: isInactive ? theme.palette.grey[500] : accountColor,
          fontWeight: 500,
          mb: 1,
        }}
      />

      {/* Balance Display */}
      <Box sx={{ mt: 1 }}>
        {!account.allowDirectPosting ? (
          // Parent account - No Direct Posting
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isInactive ? "text.disabled" : "info.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 14 }}>
                {isInactive ? "block" : "account_tree"}
              </Icon>
              {isInactive ? "Inactive Account" : "No Direct Posting"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isInactive ? "text.disabled" : "info.dark",
                fontSize: "0.7rem",
                fontStyle: "italic",
                display: "block",
                mt: 0.5,
              }}
            >
              {isInactive
                ? "Account is inactive"
                : "Balance calculated from sub-accounts"}
            </Typography>
          </Box>
        ) : account.openingBalance !== undefined ? (
          // Leaf account with balance
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: isInactive ? "text.disabled" : "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 500,
                display: "block",
              }}
            >
              Opening Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isInactive ? "text.disabled" : "text.primary",
                fontFamily: "monospace",
                fontSize: "0.875rem",
                mb: 0.5,
              }}
            >
              {account.currency} {account.openingBalance.toLocaleString()}
            </Typography>
            {/* Current Balance */}
            {account.currentBalance !== undefined && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: isInactive ? "text.disabled" : "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    display: "block",
                  }}
                >
                  Current Balance
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: isInactive
                      ? "text.disabled"
                      : account.currentBalance >= 0
                      ? "success.main"
                      : "error.main",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  }}
                >
                  {account.currency} {account.currentBalance.toLocaleString()}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          // Leaf account without balance set
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(
                isInactive
                  ? theme.palette.grey[500]
                  : theme.palette.warning.main,
                0.1
              ),
              border: `1px dashed ${alpha(
                isInactive
                  ? theme.palette.grey[500]
                  : theme.palette.warning.main,
                0.3
              )}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isInactive ? "text.disabled" : "warning.main",
                fontSize: "0.75rem",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Icon sx={{ fontSize: 12 }}>
                {isInactive ? "block" : "warning"}
              </Icon>
              {isInactive ? "Inactive Account" : "Balance Not Set"}
            </Typography>
            {!isInactive && (
              <Typography
                variant="caption"
                sx={{
                  color: "warning.dark",
                  fontSize: "0.7rem",
                  fontStyle: "italic",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Click to set opening balance
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* React Flow Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: isInactive ? theme.palette.grey[500] : accountColor,
          border: "none",
          width: 8,
          height: 8,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: isInactive ? theme.palette.grey[500] : accountColor,
          border: "none",
          width: 8,
          height: 8,
        }}
      />
    </Box>
  );
};

// Placeholder Node Component
const PlaceholderNode: React.FC<{
  data: {
    level: number;
    parentAccountId?: string;
    onAddNewAtLevel: (level: number, parentAccountId?: string) => void;
  };
}> = ({ data }) => {
  const theme = useTheme();
  const { level, parentAccountId, onAddNewAtLevel } = data;

  const handleClick = () => {
    onAddNewAtLevel(level, parentAccountId);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        minWidth: 200,
        maxWidth: 280,
        p: 2,
        bgcolor: "transparent",
        borderRadius: 2,
        border: `2px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
        cursor: "pointer",
        transition: "all 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 120,
        "&:hover": {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Icon
        sx={{
          fontSize: 32,
          color: alpha(theme.palette.primary.main, 0.6),
          mb: 1,
        }}
      >
        add_circle_outline
      </Icon>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: 600,
          textAlign: "center",
          fontSize: "0.875rem",
        }}
      >
        Add New Account
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: alpha(theme.palette.primary.main, 0.7),
          textAlign: "center",
          fontSize: "0.75rem",
        }}
      >
        Level {level}
      </Typography>
    </Box>
  );
};

// Define nodeTypes outside the component to prevent recreation on every render
const nodeTypes: NodeTypes = {
  accountNode: AccountNode,
  placeholderNode: PlaceholderNode,
};

export const AccountFlowView: React.FC<AccountFlowViewProps> = ({
  accounts,
  selectedAccount,
  onNodeClick,
  onAddSubAccount,
  onAddNewAtLevel,
}) => {
  const theme = useTheme();
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  // Convert accounts to React Flow nodes and edges with proper tree layout
  const { nodes, edges, containerHeight } = useMemo(() => {
    const flowNodes: Node[] = [];
    const flowEdges: Edge[] = [];

    // Layout constants
    const nodeWidth = 260;
    const nodeHeight = 120;
    const horizontalGap = 60;
    const verticalGap = 120;
    const startY = 80;
    const startX = 100;

    // Helper to recursively layout the tree
    function layoutTree(
      account: IChartOfAccount,
      depth: number,
      xOffset: number
    ): { width: number; nodes: Node[]; edges: Edge[]; centerX: number } {
      let children = account.children || [];
      let subtreeNodes: Node[] = [];
      let subtreeEdges: Edge[] = [];
      let subtreeWidth = 0;
      let childCenters: number[] = [];

      if (children.length === 0) {
        // Leaf node
        const x = xOffset;
        const y = startY + depth * (nodeHeight + verticalGap);
        const node: Node = {
          id: `node-${account.id}`,
          type: "accountNode",
          position: { x, y },
          data: {
            account,
            onNodeClick,
            onAddSubAccount,
          },
          style: {
            opacity: selectedAccount
              ? selectedAccount.id === account.id
                ? 1
                : 0.8
              : 1,
          },
        };
        return {
          width: nodeWidth,
          nodes: [node],
          edges: [],
          centerX: x + nodeWidth / 2,
        };
      } else {
        // Internal node
        let childX = xOffset;
        children.forEach((child: IChartOfAccount) => {
          const childLayout = layoutTree(child, depth + 1, childX);
          subtreeNodes.push(...childLayout.nodes);
          subtreeEdges.push(...childLayout.edges);
          childCenters.push(childLayout.centerX);
          childX += childLayout.width + horizontalGap;
          subtreeWidth += childLayout.width + horizontalGap;
        });
        if (children.length > 0) subtreeWidth -= horizontalGap; // Remove last gap
        // Center parent above its children
        const parentCenterX =
          (childCenters[0] + childCenters[childCenters.length - 1]) / 2;
        const parentX = parentCenterX - nodeWidth / 2;
        const parentY = startY + depth * (nodeHeight + verticalGap);
        const node: Node = {
          id: `node-${account.id}`,
          type: "accountNode",
          position: { x: parentX, y: parentY },
          data: {
            account,
            onNodeClick,
            onAddSubAccount,
          },
          style: {
            opacity: selectedAccount
              ? selectedAccount.id === account.id
                ? 1
                : 0.8
              : 1,
          },
        };
        // Edges from parent to children
        children.forEach((child: IChartOfAccount) => {
          subtreeEdges.push({
            id: `edge-${account.id}-${child.id}`,
            source: `node-${account.id}`,
            target: `node-${child.id}`,
            type: "smoothstep",
            style: {
              stroke: getAccountTypeColor(child.accountType),
              strokeWidth: 2,
              opacity: 0.6,
            },
            animated: true,
          });
        });
        return {
          width: Math.max(subtreeWidth, nodeWidth),
          nodes: [node, ...subtreeNodes],
          edges: subtreeEdges,
          centerX: parentCenterX,
        };
      }
    }

    // Start layout for each root account (multiple trees if needed)
    let globalX = startX;
    let maxY = 0;
    (accounts || []).forEach((root) => {
      const { width, nodes, edges } = layoutTree(root, 0, globalX);
      flowNodes.push(...nodes);
      flowEdges.push(...edges);
      globalX += width + 3 * horizontalGap; // Extra gap between root trees
      // Find max Y for container height
      nodes.forEach((n) => {
        if (n.position.y > maxY) maxY = n.position.y;
      });
    });

    // Add placeholders for root level (if needed)
    // (Optional: can be added similarly if you want to keep add buttons at root)

    // Calculate container height
    const containerHeight = Math.max(600, maxY + nodeHeight + 2 * verticalGap);

    return { nodes: flowNodes, edges: flowEdges, containerHeight };
  }, [accounts, selectedAccount, onNodeClick, onAddSubAccount]);

  const [reactFlowNodes, setReactFlowNodes, onNodesChange] =
    useNodesState(nodes);
  const [reactFlowEdges, setReactFlowEdges, onEdgesChange] =
    useEdgesState(edges);

  // Update React Flow state when accounts change
  useEffect(() => {
    setReactFlowNodes(nodes);
    setReactFlowEdges(edges);

    // Fit view after a short delay to ensure nodes are rendered
    if (reactFlowInstance.current) {
      setTimeout(() => {
        reactFlowInstance.current?.fitView({
          padding: 0.3,
          minZoom: 0.4,
          maxZoom: 1.2,
        });
      }, 150);
    }
  }, [nodes, edges, setReactFlowNodes, setReactFlowEdges]);

  const onConnect = useCallback(
    (params: Connection) => setReactFlowEdges((eds) => addEdge(params, eds)),
    [setReactFlowEdges]
  );

  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: `${containerHeight}px`,
        position: "relative",
        p: 2, // Add padding around the canvas
      }}
    >
      <ReactFlow
        nodes={reactFlowNodes}
        edges={reactFlowEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.3,
          minZoom: 0.4,
          maxZoom: 1.2,
        }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        style={{
          backgroundColor: alpha(theme.palette.background.default, 0.5),
        }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }} // Remove React Flow attribution
      >
        <Background
          color={alpha(theme.palette.divider, 0.1)}
          gap={20}
          size={1}
        />
        <MiniMap
          style={{
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            borderRadius: 8,
            position: "absolute",
            top: 20,
            right: 20,
            width: 150,
            height: 100,
          }}
          nodeColor={(node) => {
            const account = node.data?.account;
            return account
              ? getAccountTypeColor(account.accountType)
              : "#6B7280";
          }}
        />
      </ReactFlow>
    </Box>
  );
};
